import React, { ReactNode, useEffect, useLayoutEffect, useRef } from "react";
import { Avatar, AvatarFallback } from "../misc/Avatar";
import { cn } from "../../utils/classMerger";

export interface ChatMessage {
    id: string;
    from: "bot" | "me";
    lead?: string;
    text: ReactNode;
}

interface NicheChatThreadProps {
    messages: ChatMessage[];
    typing?: boolean;
    systemLine?: string;
    // A message id to start the scroll at (the review's summary): its top at the top of the region, not the newest row.
    startAt?: string;
    // Rendered after the last message (the write-up failure's Try again, the status line).
    children?: ReactNode;
    // Below the conversation, still inside its scroll region (the review's form).
    footer?: ReactNode;
}

const BUBBLE = "max-w-[82%] rounded-2xl px-3 py-2 text-sm break-words";
// Within this many px of the end counts as reading the newest row.
const AT_BOTTOM = 8;
const atBottomOf = (box: HTMLElement): boolean => box.scrollTop + box.clientHeight >= box.scrollHeight - AT_BOTTOM;

// The bot's side of a row: the Marabot avatar, then its bubble.
const BotSide: React.FC<{ className?: string; children: ReactNode }> = ({ className, children }) => (
    <>
        <Avatar className="h-7 w-7">
            <AvatarFallback aria-hidden="true">M</AvatarFallback>
        </Avatar>
        <div className={cn(BUBBLE, "rounded-bl-sm bg-gray-100 text-gray-900", className)}>{children}</div>
    </>
);

// The interview as a messaging thread: the bot's questions on the left, the agent's answers on the
// right. Not a live region: the page's question announcer already reads each new question once.
// It scrolls inside its own region, which takes whatever height the card leaves it.
const NicheChatThread: React.FC<NicheChatThreadProps> = ({ messages, typing = false, systemLine, startAt, children, footer }) => {
    const region = useRef<HTMLDivElement>(null);
    const list = useRef<HTMLOListElement>(null);
    // Whether the agent is reading the newest row (kept up to date as they scroll).
    const atBottom = useRef(true);
    // The message the scroll last started at, so it starts there once per entry.
    const startedAt = useRef<string>();
    const startIndex = startAt === undefined ? -1 : messages.findIndex((m) => m.id === startAt);
    // Only the region scrolls, never the page. It follows the newest row when a message lands or the
    // typing starts, not on every render, so an agent who scrolled up to read stays put. Started at a
    // message, that message's top goes to the top instead, once per entry: a later change (an older api's
    // Save or Discard shortens the list before the reload) leaves the agent where they are. The region
    // is positioned, so a row's offsetTop is measured from it.
    useLayoutEffect(() => {
        const box = region.current;
        if (!box) return;
        const row = startIndex < 0 ? undefined : (list.current?.children[startIndex] as HTMLElement | undefined);
        if (row) {
            if (startedAt.current === startAt) return;
            startedAt.current = startAt;
            box.scrollTop = row.offsetTop;
        } else {
            startedAt.current = undefined;
            box.scrollTop = box.scrollHeight;
        }
        atBottom.current = atBottomOf(box);
    }, [messages.length, typing, startIndex, startAt]);
    // When the region itself gets shorter or taller (the box grows to four rows, a note shows above it, the
    // window resizes), an agent reading the newest row stays on it; one who scrolled up is left alone.
    useEffect(() => {
        const box = region.current;
        if (!box || typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(() => {
            if (atBottom.current) box.scrollTop = box.scrollHeight;
        });
        observer.observe(box);
        return () => observer.disconnect();
    }, []);

    return (
        <div
            ref={region}
            data-testid="niche-scroll"
            // Focusable so the keyboard alone can scroll back: Safari won't Tab to a scroller with nothing focusable in it.
            tabIndex={0}
            role="region"
            aria-label="Conversation"
            // Positioned, so hidden text inside (the sr-only speaker labels, "Thinking…") is clipped and scrolls
            // with it; otherwise it hangs below the card and a long thread stretches the page.
            className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
            onScroll={(e) => { atBottom.current = atBottomOf(e.currentTarget); }}
        >
            <div className="space-y-3 px-6 py-4">
                {systemLine && <p className="text-center text-xs text-gray-500">{systemLine}</p>}
                <ol ref={list} aria-label="Interview conversation" className="space-y-2.5">
                    {messages.map((m) =>
                        m.from === "bot" ? (
                            <li key={m.id} className="flex items-end gap-2">
                                {/* Side and colour are all that tell the speakers apart on screen. */}
                                <span className="sr-only">Marabot: </span>
                                <BotSide>
                                    {m.lead && <span className="block text-[13px] font-semibold text-primary">{m.lead}</span>}
                                    {m.text}
                                </BotSide>
                            </li>
                        ) : (
                            <li key={m.id} className="flex justify-end">
                                <span className="sr-only">You: </span>
                                {/* The agent's own line breaks (Shift+Enter) survive. */}
                                <div className={cn(BUBBLE, "whitespace-pre-wrap rounded-br-sm bg-primary text-white")}>{m.text}</div>
                            </li>
                        ),
                    )}
                    {typing && (
                        <li data-testid="niche-typing" aria-hidden="true" className="flex items-end gap-2">
                            <BotSide className="flex items-center gap-1 py-3">
                                {[0, 150, 300].map((ms) => (
                                    <span key={ms} className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-bounce motion-reduce:animate-none" style={{ animationDelay: `${ms}ms` }} />
                                ))}
                            </BotSide>
                        </li>
                    )}
                </ol>
                {children}
            </div>
            {footer}
        </div>
    );
};

export default NicheChatThread;
